import { Link, type LinkProps, useLocation } from '@tanstack/react-router';
import { Fragment } from 'react';

import { Card } from '@/components/Card/card';
import Icon from '@/components/Icon/Icon';
import type { IconName } from '@/components/Icon/iconMap';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/cn';

export type NavigationSectionGroup = {
  titleLabel: string;
  icon?: IconName;
  to: Exclude<LinkProps['to'], undefined>;
  hash?: string;
  sections?: NavigationSection[];
};

export type NavigationSection = {
  label: string;
  icon?: IconName;
  hash: string;
};

type NavigationCardStyle = 'default' | 'compact' | 'spacious';

export type NavigationCardProps = {
  sections: NavigationSectionGroup[];
  style?: NavigationCardStyle;
  showGroupSeparator?: boolean;
};

const styleMap = {
  default: {
    card: 'py-5',
    vStack: 'gap-2 my-1',
    link: 'flex min-h-9 items-center rounded-md px-3 transition-colors',
  },
  compact: {
    card: 'py-3',
    vStack: 'gap-1 my-0.5',
    link: 'flex min-h-7 items-center rounded-md px-2 transition-colors',
  },
  spacious: {
    card: 'py-7',
    vStack: 'gap-3 my-2',
    link: 'flex min-h-11 items-center rounded-md px-4 transition-colors',
  },
};

export default function NavigationCard({
  sections: sectionMap,
  style = 'default',
  showGroupSeparator = false,
}: NavigationCardProps) {
  const styles = styleMap[style];
  const location = useLocation();
  const currentHash = location.hash.replace('#', '');

  return (
    <Card
      className={cn(
        styles.card,
        'bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md',
      )}
    >
      <VStack className="gap-2 px-5">
        {sectionMap.map(({ titleLabel, icon, to, hash, sections }, index) => (
          <Fragment key={titleLabel}>
            {showGroupSeparator && index > 0 && <Separator className="my-1" />}
            <VStack className={styles.vStack}>
              {sections && sections.length > 0 ? (
                <>
                  {icon ? (
                    <Icon iconName={icon} text={titleLabel} />
                  ) : (
                    <Text
                      variant="label"
                      className="text-neutral-muted-on-fill"
                    >
                      {titleLabel}
                    </Text>
                  )}
                  {sections.map(({ label, icon, hash }) => (
                    <Link
                      to={to}
                      key={hash}
                      hash={hash}
                      className={cn(
                        styles.link,
                        currentHash === hash
                          ? 'bg-primary-muted-fill text-primary-on-muted-fill'
                          : 'text-neutral-muted-on-fill hover:bg-primary-muted-fill hover:text-primary hover:shadow-xs',
                      )}
                      resetScroll={true}
                    >
                      <HStack className="items-center gap-1.5">
                        {icon && (
                          <Icon
                            colorVariant={
                              currentHash === hash
                                ? 'primary'
                                : 'neutralOnMutedFill'
                            }
                            iconName={icon}
                          />
                        )}
                        <Text variant="buttonLabel" breakWord={true}>
                          {label}
                        </Text>
                      </HStack>
                    </Link>
                  ))}
                </>
              ) : (
                <Link
                  to={to}
                  key={hash || titleLabel}
                  hash={hash}
                  className={cn(
                    styles.link,
                    currentHash === hash
                      ? 'bg-primary-muted-fill text-primary-on-muted-fill'
                      : 'text-neutral-muted-on-fill hover:bg-primary-muted-fill hover:text-primary hover:shadow-xs',
                  )}
                  resetScroll={true}
                >
                  <HStack className="items-center gap-1.5">
                    {icon && (
                      <Icon
                        colorVariant={
                          currentHash === hash
                            ? 'primary'
                            : 'neutralOnMutedFill'
                        }
                        iconName={icon}
                        className="stroke-current"
                      />
                    )}
                    <Text variant="buttonLabel" breakWord={true}>
                      {titleLabel}
                    </Text>
                  </HStack>
                </Link>
              )}
            </VStack>
          </Fragment>
        ))}
      </VStack>
    </Card>
  );
}
